import { ScheduleType } from 'mbs-ui-shared@models/backup/schedule-models';
import { WizardHelpersSettings } from 'wizards/helpers/bases/base-for-steps-helper';
import { PlanDataFromStepsHelper } from 'wizards/helpers/plan-data-from-steps-helper/plan-data-from-steps-helper';
import { RestorePlanFromStepsHelper } from 'wizards/helpers/restore-plan-from-steps-helper/restore-plan-from-steps-helper';
import { RestoreIbbFormModel } from 'wizards/models/restoreIBB/restore-ibb-form-model';
import {
  CloudRestoreTypes,
  RestoreAsDiskEnum,
  RestoreToHyperVTypes,
  RestoreToVMNeedHostTypes,
  RestoreToVMTypes
} from 'wizards/models/type-data-models';
import { BaseForPlanHelper } from '../../models/base/base-models/base-for-plan-helper';

export class RestoreIbbPlanFromStepsHelpers extends BaseForPlanHelper {
  public static RestoreIBBModel: RestoreIbbFormModel = null;

  public static getUpdatedPlan(plan, model: RestoreIbbFormModel, settings: WizardHelpersSettings): any {
    this.RestoreIBBModel = model;
    this.updateSchedules(
      +ScheduleType[model.scheduleStep.ScheduleType] === ScheduleType.recurring,
      this.RestoreIBBModel.scheduleAdvancedStep
    );
    const newPlan = Object.assign({}, plan);

    this.updateModel(newPlan, settings);

    return newPlan;
  }

  static updateModel(newPlan, settings: WizardHelpersSettings): void {
    const azureOrEC2 = CloudRestoreTypes.includes(this.RestoreIBBModel.typeDataStep.restoreAs);

    RestorePlanFromStepsHelper.updateNewPlanByNameStep(newPlan, this.RestoreIBBModel.planNameStep);
    PlanDataFromStepsHelper.updateNewPlanByWhereBackupStep(newPlan, this.RestoreIBBModel.whereBackupStep);
    RestorePlanFromStepsHelper.updateNewPlanByBackupToRestoreStep(newPlan, this.RestoreIBBModel.backupToRestoreStep);
    RestorePlanFromStepsHelper.updateNewPlanByRestorePointStep(newPlan, this.RestoreIBBModel.restorePointStep, settings, true);
    RestorePlanFromStepsHelper.updateNewPlanByTypeDataStep(newPlan, this.RestoreIBBModel.typeDataStep, settings.planType);

    if (azureOrEC2) this.updateAzureOrEC2AndTemporary(this.RestoreIBBModel.typeDataStep.restoreAs, newPlan);

    RestorePlanFromStepsHelper.updateNewPlanByPartitionsStep(newPlan, this.RestoreIBBModel.partitionsStep);

    if (!azureOrEC2 && RestoreToVMTypes.includes(this.RestoreIBBModel.typeDataStep.restoreAs)) {
      const isProxmox = this.RestoreIBBModel.typeDataStep.restoreAs === RestoreAsDiskEnum.RestoreToProxmox;
      if (RestoreToVMNeedHostTypes.includes(this.RestoreIBBModel.typeDataStep.restoreAs)) {
        RestorePlanFromStepsHelper.updateHostIbb(newPlan, this.RestoreIBBModel.selectHostStep, isProxmox);
      }

      if (RestoreToHyperVTypes.includes(this.RestoreIBBModel.typeDataStep.restoreAs)) {
        RestorePlanFromStepsHelper.updateVmSettingsHyperV(
          newPlan,
          this.RestoreIBBModel.vmSettingsStep,
          this.RestoreIBBModel.partitionsStep
        );
      } else if (this.RestoreIBBModel.typeDataStep.restoreAs === RestoreAsDiskEnum.RestoreToVMwareVSphere) {
        RestorePlanFromStepsHelper.updateVmSettings(newPlan, this.RestoreIBBModel.vmSettingsStep, this.RestoreIBBModel.partitionsStep);
      } else {
        RestorePlanFromStepsHelper.updateVmSettings(
          newPlan,
          this.RestoreIBBModel.vmSettingsStep,
          this.RestoreIBBModel.partitionsStep,
          true
        );
      }
    } else if (!azureOrEC2) {
      RestorePlanFromStepsHelper.updateNewPlanByDestinationStep(
        newPlan,
        this.RestoreIBBModel.destinationStep,
        this.RestoreIBBModel.partitionsStep
      );
    }

    RestorePlanFromStepsHelper.updateNewPlanByEncryptionOptionsStep(newPlan, this.RestoreIBBModel.encryptionOptionsStep);

    if (!azureOrEC2) {
      RestorePlanFromStepsHelper.updateNewPlanByScheduleStep(
        newPlan,
        this.RestoreIBBModel.scheduleStep,
        this.RestoreIBBModel.scheduleAdvancedStep,
        settings.isUnix,
        settings.isNBF
      );

      PlanDataFromStepsHelper.updateNewPlanByPrePostActionsStep(
        newPlan,
        this.RestoreIBBModel.prePostActionsStep,
        !settings.allowPrePostActions,
        false,
        true
      );
      PlanDataFromStepsHelper.updateNewPlanByNotificationStep(newPlan, this.RestoreIBBModel.notificationStep);
    }

    this.setDataFromRestore(newPlan, settings, this.RestoreIBBModel);
    PlanDataFromStepsHelper.preparePlanForSending(newPlan);
  }

  private static updateAzureOrEC2AndTemporary(restoreAs: RestoreAsDiskEnum, newPlan): void {
    const compName = this.RestoreIBBModel.computerToRestoreStep?.computerName;
    if (restoreAs === RestoreAsDiskEnum.AVM) {
      RestorePlanFromStepsHelper.updatePlanFromAzureTargetStep(newPlan, this.RestoreIBBModel.targetInstanceAzureStep, compName);
      RestorePlanFromStepsHelper.updatePlanFromAzureTemporaryStep(newPlan, this.RestoreIBBModel.temporaryInstanceAzureStep);
    } else if (restoreAs === RestoreAsDiskEnum.EC2) {
      RestorePlanFromStepsHelper.updatePlanFromEC2Target(
        newPlan,
        this.RestoreIBBModel.targetInstanceStep,
        compName,
        this.RestoreIBBModel.partitionsStep?.bootMode
      );
      RestorePlanFromStepsHelper.updatePlanFromEC2TemporaryStep(newPlan, this.RestoreIBBModel.temporaryInstanceStep, compName);
    }

    newPlan.RestoreDiskConfiguration = RestorePlanFromStepsHelper.getDisksFromRestorePartitionsVM(this.RestoreIBBModel.partitionsStep);

    newPlan.IsRemoteRestore = true;
    newPlan.ForceFullSchedule = { Enabled: false };
    newPlan.Schedule = { Enabled: false };
    newPlan.ScheduleDiff = { Enabled: false };
    newPlan.ScheduleTLog = { Enabled: false };
    newPlan.Notification = {
      GenerateReport: true,
      OnlyOnFailure: true,
      SendNotification: false,
      SubjectString: null
    };
    newPlan.Actions = { Pre: null, Post: null };
  }
}
